import { useGetCountriesQuery } from '@pm/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type CountryCode = 'CA' | 'US';

const useProvinceStates = (countryCode: CountryCode) => {
  const { data } = useGetCountriesQuery();
  if (!data) return undefined;

  const country = data.countries.find(({ code }) => code === countryCode);
  return country?.provinces;
};

export const useCanadianProvinces = () => useProvinceStates('CA');

// Deliberately unfiltered by `supported`. That field is computed per request
// from each brand's supported province codes, which are Canadian, so every US
// state resolves to false — filtering on it here would return nothing.
export const useUsStates = () => useProvinceStates('US');

/**
 * Dropdown options keyed by province/state `code` — what the shipping address
 * form persists. For the id-keyed equivalent see `useCanadianProvincesOptions`.
 */
export const useProvinceStateOptions = ({
  country,
}: {
  country: CountryCode;
}) => {
  const { t } = useTranslation('global');
  const { data, loading } = useGetCountriesQuery();
  const provinceStates = data?.countries.find(
    ({ code }) => code === country,
  )?.provinces;

  const options = useMemo(
    () =>
      (
        provinceStates?.map(({ code, name }) => ({
          key: code,
          // Only Canadian names have translation keys. US state names arrive
          // from the API in English, which is what USPS uses.
          displayValue:
            country === 'CA' ? t(`Provinces.${name as ProvinceName}`) : name,
        })) ?? []
      ).sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
    [provinceStates, country, t],
  );

  // The API returns seed insertion order, which is alphabetical for Canada by
  // luck and not at all for the US — hence the sort above.
  return { data: options, loading };
};

export const useSupportedCanadianProvinces = () =>
  useCanadianProvinces()?.filter((province) => province.supported) ?? [];

/**
 * Dropdown options keyed by province `id`. Kept for the profile forms that
 * submit ids; anything storing a code wants `useProvinceStateOptions`.
 */
export const useCanadianProvincesOptions = () => {
  const { t } = useTranslation('global');
  const { data, loading } = useGetCountriesQuery();
  const canada = data?.countries.find((country) => country.code === 'CA');
  const provinces =
    canada?.provinces.map(({ id, name }) => {
      const provinceName = name as ProvinceName;

      return {
        key: id,
        displayValue: t(`Provinces.${provinceName}`),
      };
    }) ?? [];

  return {
    data: provinces,
    loading,
  };
};

export const useUnsupportedCanadianProvinces = () =>
  useCanadianProvinces()?.filter((province) => !province.supported);

export type ProvinceName =
  | 'Alberta'
  | 'British Columbia'
  | 'Manitoba'
  | 'New Brunswick'
  | 'Newfoundland and Labrador'
  | 'Northwest Territories'
  | 'Nova Scotia'
  | 'Nunavut'
  | 'Ontario'
  | 'Prince Edward Island'
  | 'Quebec'
  | 'Saskatchewan'
  | 'Yukon';
